import { useState } from "react";
import AccountLayout from "../components/AccountLayout.jsx";
import StatusPill from "../components/billing/StatusPill.jsx";
import PriceTag from "../components/billing/PriceTag.jsx";
import PlanCard from "../components/billing/PlanCard.jsx";
import GradientSummaryCard from "../components/billing/GradientSummaryCard.jsx";
import CheckoutRecoveryBanner from "../components/billing/CheckoutRecoveryBanner.jsx";
import { createCheckoutSession } from "../lib/stripe.js";
import { isPayingHeycoachSubscriber } from "../lib/heycoachSubscription.js";
import { useSubscription } from "../hooks/useSubscription.js";
import { useCheckoutRecovery } from "../hooks/useCheckoutRecovery.js";
import { formatBillingInstant } from "../lib/stripeInstant.js";
import { formatMoneyFromMinorUnits } from "../lib/planCatalog.js";
import { billingErrorCopy } from "../lib/userFacingErrors.js";
import {
  CurrentPlanSummarySkeleton,
  PlanCardSkeletonGrid,
} from "../components/loading/SkeletonBlocks.jsx";
import { usePlanCatalog } from "../hooks/usePlanCatalog.js";

function planIsFixedTerm(plan) {
  return (
    plan.primaryAmountCents != null &&
    plan.monthlyAmountCents == null &&
    plan.yearlyAmountCents == null
  );
}

function formatTermSuffix(termMonths) {
  const n = Number(termMonths);
  if (!Number.isFinite(n) || n <= 0) return "one-time";
  return n === 1 ? "for 1 month" : `for ${n} months`;
}

function formatUnknownPlanLabel(planId) {
  return String(planId)
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function humanizeStripeStatus(status) {
  const s = String(status ?? "").trim().toLowerCase();
  if (!s) return "";
  const map = {
    active: "Active",
    trialing: "Trialing",
    past_due: "Past due",
    paused: "Paused",
    canceled: "Canceled",
    unpaid: "Unpaid",
    incomplete: "Incomplete",
    incomplete_expired: "Incomplete expired",
  };
  return map[s] ?? s.charAt(0).toUpperCase() + s.slice(1);
}

export default function PlanPage() {
  const [loading, setLoading] = useState(null);
  const {
    subscription,
    isLoading: subLoading,
    error: subError,
    refetch: refetchSubscription,
  } = useSubscription();
  const { plans, isLoading: catalogLoading, error: catalogError, catalogHint, findTier } = usePlanCatalog({ audience: "coach" });

  const {
    pendingSessionId: pendingCheckoutSessionId,
    recoverBusy,
    recoverErr,
    retry: retryCheckoutRecovery,
  } = useCheckoutRecovery({
    subLoading,
    alreadyResolved: Boolean(subscription),
    onRecovered: refetchSubscription,
  });

  const trimmed = subscription?.planId != null ? String(subscription.planId).trim() : "";
  const rawPlanId = trimmed || null;
  const hasStripeSubscriptionRecord = Boolean(
    String(subscription?.stripeSubscriptionId ?? "").trim(),
  );
  const hasStripeCustomerRecord = Boolean(
    String(subscription?.stripeCustomerId ?? "").trim(),
  );
  const hasStripeBillingRecord =
    hasStripeSubscriptionRecord || hasStripeCustomerRecord;
  const statusNorm = String(subscription?.status ?? "").trim().toLowerCase();

  const subscriptionLooksPaid =
    subscription && isPayingHeycoachSubscriber(subscription);
  const stripeTierPlan = rawPlanId ? findTier(rawPlanId) : null;
  const activePlanId = subscriptionLooksPaid ? rawPlanId : null;

  const planLabelFromDoc =
    stripeTierPlan?.name ??
    (rawPlanId ? formatUnknownPlanLabel(rawPlanId) : null);

  const currentPlanTitle = subLoading
    ? "—"
    : subError && !subscription
      ? "Could not load plan"
      : !subscription
        ? "None"
        : subscriptionLooksPaid
          ? (planLabelFromDoc ?? "Your subscription")
          : "No active plan";

  const showSummaryPricing = Boolean(
    subscriptionLooksPaid && subscription && (stripeTierPlan || rawPlanId),
  );

  const currentPeriodEndLabel = formatBillingInstant(subscription?.currentPeriodEnd);

  async function handleSelectPlan(planId) {
    if (loading) return;
    setLoading(planId);
    try {
      const { url } = await createCheckoutSession(planId, "month");
      window.location.href = url;
    } catch (err) {
      alert(err.message);
      setLoading(null);
    }
  }

  return (
    <AccountLayout>
      {pendingCheckoutSessionId && (
        <CheckoutRecoveryBanner
          className="mb-5"
          description={billingErrorCopy.linkSubscription}
          busy={recoverBusy}
          error={recoverErr}
          onRetry={retryCheckoutRecovery}
          retryLabel="Try again"
        />
      )}

      { }
      <GradientSummaryCard>
        {subLoading ? (
          <CurrentPlanSummarySkeleton />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs text-hc-text-dim">Current plan</p>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-2xl font-extrabold capitalize tracking-[0.07px] text-hc-text">
                  {currentPlanTitle}
                </span>
                {subscriptionLooksPaid && <StatusPill variant="success">Active</StatusPill>}
                {!subscriptionLooksPaid && hasStripeBillingRecord && statusNorm && (
                  <StatusPill variant="neutral">{humanizeStripeStatus(statusNorm)}</StatusPill>
                )}
              </div>
              {subscription && !subscriptionLooksPaid && rawPlanId && (
                <p className="mt-2 text-xs text-hc-text-dim">
                  Previous: {planLabelFromDoc ?? formatUnknownPlanLabel(rawPlanId)}
                </p>
              )}
              {subError && (
                <p className="mt-2 max-w-lg text-xs text-hc-error">{subError}</p>
              )}
            </div>
            {showSummaryPricing && (
              <div className="text-right">
                {stripeTierPlan?.monthlyAmountCents != null ? (
                  <>
                    <p className="text-2xl font-extrabold tracking-[0.07px] text-hc-text">
                      {formatMoneyFromMinorUnits(
                        stripeTierPlan.monthlyAmountCents,
                        stripeTierPlan.currency,
                      )}
                    </p>
                    {currentPeriodEndLabel && (
                      <p className="text-xs text-hc-text-dim">
                        per month · renews {currentPeriodEndLabel}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-hc-text">{currentPlanTitle}</p>
                    {currentPeriodEndLabel && (
                      <p className="mt-1 text-xs text-hc-text-dim">Renews {currentPeriodEndLabel}</p>
                    )}
                    <p className="mt-1 text-xs text-hc-text-dim">
                      Manage billing for pricing details.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </GradientSummaryCard>

      { }
      <div>
        <h2 className="mb-5 text-sm font-semibold tracking-[-0.15px] text-hc-text">
          Compare plans
        </h2>

        {catalogError && (
          <p className="mb-4 text-xs text-red-400 break-words">{catalogError}</p>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          {catalogLoading && plans.length === 0 ? (
            <PlanCardSkeletonGrid count={3} tall className="col-span-full sm:grid-cols-3" />
          ) : null}
          {!catalogLoading && plans.length === 0 && !catalogError ? (
            <p className="col-span-full text-sm text-hc-text-dim">
              {catalogHint ??
                "Plans aren't available right now. Please "}
              {!catalogHint && (
                <>
                  contact{" "}
                  <a href="/support" className="text-hc-primary underline hover:opacity-90">
                    support
                  </a>{" "}
                  if you need help choosing a plan.
                </>
              )}
            </p>
          ) : null}
          {plans.map((plan) => {
            const isCurrent =
              subscriptionLooksPaid &&
              rawPlanId != null &&
              plan.id.toLowerCase() === rawPlanId.toLowerCase();
            const isFixedTerm = planIsFixedTerm(plan);
            const perMoLabel =
              plan.monthlyAmountCents != null
                ? formatMoneyFromMinorUnits(plan.monthlyAmountCents, plan.currency)
                : "—";
            const fixedTermLabel = isFixedTerm
              ? formatMoneyFromMinorUnits(plan.primaryAmountCents, plan.currency)
              : null;
            const isLoading = loading === plan.id;
            const currentTierMonthly = stripeTierPlan?.monthlyAmountCents ?? null;

            return (
              <PlanCard
                key={plan.id}
                tone={isCurrent ? "highlighted" : "surface"}
                title={plan.name}
                badge={isCurrent && <StatusPill variant="primary">Your plan</StatusPill>}
                footer={
                  <button
                    type="button"
                    disabled={isCurrent || !!loading}
                    onClick={() => !isCurrent && handleSelectPlan(plan.id)}
                    className={`mt-5 w-full rounded-[10px] px-4 py-2 text-sm font-semibold transition disabled:opacity-50 ${isCurrent
                      ? "cursor-default border border-hc-surface-border text-hc-text-dim"
                      : "bg-hc-primary text-white hover:opacity-90"
                      }`}
                  >
                    {isLoading
                      ? "Redirecting…"
                      : isCurrent
                        ? "Current plan"
                        : isFixedTerm
                          ? "Get started"
                          : activePlanId &&
                            (plan.monthlyAmountCents ?? 0) < (currentTierMonthly ?? Infinity)
                            ? "Downgrade"
                            : "Upgrade"}
                  </button>
                }
              >
                {isFixedTerm ? (
                  <>
                    <PriceTag amount={fixedTermLabel} suffix={formatTermSuffix(plan.termMonths)} />
                    {Number(plan.termMonths) > 0 && (
                      <p className="mt-0.5 text-xs text-hc-text-dim">
                        One payment for the full term
                      </p>
                    )}
                  </>
                ) : (
                  <PriceTag amount={perMoLabel} suffix="/mo" />
                )}
                <p className="mt-2 text-sm text-hc-text-dim">{plan.description}</p>
              </PlanCard>
            );
          })}
        </div>
      </div>
    </AccountLayout>
  );
}
